import React, { useEffect, useMemo, useState } from 'react';
import { Input, InputProps } from 'antd';
import { DateTime } from 'luxon';
import Datestamp from 'vl-common/src/components/Datestamp';
import { DateInputItem, DateInputLegend, DateInputPreview, Flex } from './styled';
import cn from 'classnames';
import {
  getDayStatus,
  getInvalidDateMessage,
  getMonthStatus,
  getTemplatedProps,
  getYearStatus,
  toDateInput,
  toISODate
} from './helpers';

const BASE_PROPS: InputProps = { inputMode: 'numeric', pattern: '[0-9]*' };

interface DateInputProps {
  disabled?: boolean;
  isDob?: boolean;
  /** **Required**: Renders as a fieldset, which must have a legend element for accessibility. */
  label: string;
  required?: boolean;
  value?: string;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
  hideLabel?: boolean;
  firstInputId?: string;
  /** Optional alignment for the date preview sub-label. Inherits from parent if omitted. */
  alignSubLabel?: 'left' | 'center' | 'right' | 'inherit';
}

/**
 * A [GOV.UK-style date input](https://design-system.service.gov.uk/components/date-input/) with minimal API
 * surface. Intended to be compatible with Ant Design's [Form](https://4x.ant.design/components/form/)
 * component.
 *
 * The component will provide real-time user feedback to flag **impossible** dates, but does not validate
 * the input (e.g. it may supply a datestring of `"2020-thing-01"`). The consumer is responsible
 * for parsing and validating the input.
 */
export const DateInput = ({
  disabled,
  isDob = true,
  label,
  required = false,
  onBlur,
  onChange,
  value,
  hideLabel = false,
  firstInputId,
  alignSubLabel,
  ...rest
}: DateInputProps) => {
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  const isoDate = useMemo(() => toISODate({ day, month, year }), [day, month, year]);
  const jsDate = useMemo(() => {
    if (!isoDate) return undefined;

    return DateTime.fromISO(isoDate).toJSDate();
  }, [isoDate]);

  const status = useMemo(
    () => ({
      day: getDayStatus(day, month, year),
      month: getMonthStatus(month),
      year: getYearStatus(year)
    }),
    [day, month, year]
  );

  const invalidDateMessage = useMemo(() => getInvalidDateMessage(status.day, status.month, status.year), [status]);

  useEffect(() => {
    if (!value) return;
    if (!DateTime.fromISO(String(value)).isValid) return;

    const { day, month, year } = toDateInput(String(value));

    setDay(day);
    setMonth(month);
    setYear(year);
  }, [value]);

  const sharedProps: InputProps = {
    ...BASE_PROPS,
    disabled,
    onBlur: () => {
      if (onBlur) onBlur(`${year}-${month}-${day}`);
    },
    onChange: ({ target: { name, value } }) => {
      if (!onChange) return;
      if (name === 'day') {
        setDay(value);
        onChange(`${year}-${month}-${value}`);
      }
      if (name === 'month') {
        setMonth(value);
        onChange(`${year}-${value}-${day}`);
      }
      if (name === 'year') {
        setYear(value);
        onChange(`${value}-${month}-${day}`);
      }
    }
  };

  const displayInputs = [
    {
      label: 'Day',
      name: 'day',
      value: day
    },
    {
      label: 'Month',
      name: 'month',
      value: month
    },
    {
      label: 'Year',
      name: 'year',
      value: year
    }
  ] as const;

  return (
    <div>
      <Input
        {...rest}
        disabled={disabled}
        onChange={(e) => {
          if (onChange) onChange(e.target.value);
        }}
        data-testid="date-input-iso-date"
        value={value}
        hidden
      />
      <fieldset>
        {!hideLabel && (
          <DateInputLegend className="ant-form-item-label">
            {/* Antd 'required' styles are annoyingly scoped to label elements and label is the wrong element to use here.
              ...however, it's doesn't actually seem to upset the a11y tree if we use a label here. */}
            <label className={cn({ 'ant-form-item-required': required })}>{label}</label>
          </DateInputLegend>
        )}

        <DateInputPreview style={{ color: '#000000', textAlign: alignSubLabel }}>
          {jsDate ? (
            <Datestamp format="Long Date with day-of-week - Thursday, March 3rd, 2022" date={jsDate} />
          ) : (
            invalidDateMessage
          )}
        </DateInputPreview>

        <Flex>
          {displayInputs.map(({ label, name, value }, index) => (
            <DateInputItem key={name}>
              <label htmlFor={name}>{label}</label>
              <Input
                {...rest}
                {...sharedProps}
                {...getTemplatedProps(name, isDob)}
                status={status[name]}
                value={value}
                id={index === 0 && firstInputId ? firstInputId : name}
              />
            </DateInputItem>
          ))}
        </Flex>
      </fieldset>
    </div>
  );
};
